import { TutorialDefinition } from './tutorialDefinitions';

/**
 * Exploration and world map tutorials - Phase 4 Implementation
 * Covers hex world navigation, travel points, and resource gathering
 */

export const WORLD_MAP_TUTORIAL: TutorialDefinition = {
  key: 'world-map-basics',
  title: 'World Map & Exploration',
  description: 'Navigate the hex world, discover new locations, and explore the realm',
  category: 'exploration',
  priority: 'high',
  estimatedMinutes: 4,
  icon: '🗺️',
  prerequisites: ['battle-basics'], // Should understand combat before exploring
  steps: [
    {
      id: 'welcome',
      title: 'Welcome to the World Map!',
      content: 'The hex world is your gateway to adventure! Each hexagonal tile contains resources, monsters, or special locations. Let\'s learn how to navigate this vast realm.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'travel-points',
      title: 'Travel Points (TP)',
      content: 'Travel Points are required to move between tiles. You regenerate 1 TP every 2 minutes (when below 3 TP) or every 10 minutes (when at 3+ TP). Maximum is 10 TP. Plan your journeys wisely!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'current-location',
      title: 'Your Current Location',
      content: 'The tile with a pulsing glow is your current location. You can only travel to adjacent tiles (tiles touching your current hex). Click on an adjacent tile to view its details.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'tile-types',
      title: 'Tile Types & Biomes',
      content: 'Tiles have different biomes: Plains, Forest, Mountain, Desert, Swamp, and more. Each biome offers different resources and enemies. Hover over tiles to see what they contain!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'locked-tiles',
      title: 'Locked Tiles',
      content: 'Red locked tiles require you to win battles to unlock them. Win 3 battles on your current tile to unlock adjacent tiles. Progress is shown as "0/3", "1/3", "2/3" battles won.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'resources',
      title: 'Resource Nodes',
      content: 'Green resource icons show gathering spots. Tiles can have Ore, Wood, Herbs, Gems, and more. Resources have stock limits and regenerate over time. Gather materials for crafting!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'monsters',
      title: 'Monster Spawns',
      content: 'Red monster icons indicate enemy encounters. Click "Start Battle" to fight monsters and earn XP, loot, and unlock progress. Choose your party wisely before engaging!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'towns',
      title: 'Towns & Special Locations',
      content: 'Town tiles (🏰) are special locations with boss battles, quests, and bonuses. Liberate towns by defeating their bosses to gain access to fast travel and powerful benefits.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'map-controls',
      title: 'Map Navigation',
      content: 'Use zoom buttons or mouse wheel to zoom in/out. Click and drag to pan around the map. The "Reset View" button centers the map on your location. Explore at your own pace!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'exploration-tips',
      title: 'Exploration Tips',
      content: 'Plan routes to maximize TP efficiency. Unlock tiles strategically to reach valuable resources. Fight battles to level up and unlock new areas. The world is yours to conquer!',
      position: 'center',
      type: 'info'
    }
  ]
};

export const GATHERING_TUTORIAL: TutorialDefinition = {
  key: 'gathering-basics',
  title: 'Resource Gathering',
  description: 'Learn to gather materials for crafting and progression',
  category: 'exploration',
  priority: 'medium',
  estimatedMinutes: 3,
  icon: '⛏️',
  prerequisites: ['world-map-basics'], // Should understand world map first
  steps: [
    {
      id: 'welcome',
      title: 'Resource Gathering System',
      content: 'Gathering is how you collect materials for crafting equipment and items. You can gather passively over time, even when offline! Let\'s learn how it works.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'gathering-nodes',
      title: 'Gathering Nodes',
      content: 'Each tile shows available resources with green icons. Ore (⛏️), Wood (🪵), Herbs (🌿), and Gems (💎) are common material types. Stock amounts show how much is available.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'start-gathering',
      title: 'Starting a Gathering Activity',
      content: 'Click on a tile with resources, then click "Start Gathering" and select the material type. Your character will gather that resource every 5 minutes automatically!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'offline-gathering',
      title: 'Offline Progression',
      content: 'Gathering continues even when you\'re offline! When you return, you can collect all accumulated materials. This means you\'re always making progress, even when away.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'class-bonuses',
      title: 'Class Gathering Bonuses',
      content: 'Different classes excel at different gathering! Warriors get more Ore, Mages get more Herbs, and Rogues get more Wood. Your stats also affect gathering yield and rarity!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'rarity-system',
      title: 'Material Rarity',
      content: 'Materials come in 5 rarities: Common (gray), Uncommon (green), Rare (blue), Epic (purple), and Legendary (gold). Higher level characters gather rarer materials more often!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'resource-depletion',
      title: 'Resource Stock & Regeneration',
      content: 'Resource nodes have limited stock that depletes when gathered. They regenerate over time - Common resources respawn fastest, Legendary slowest. Plan your gathering routes!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'gathering-tips',
      title: 'Gathering Tips',
      content: 'Leave gathering running while you battle or craft! Switch characters to different tiles for variety. Check back regularly to collect materials. Gathering is key to crafting powerful equipment!',
      position: 'center',
      type: 'info'
    }
  ]
};

export const explorationTutorials = [WORLD_MAP_TUTORIAL, GATHERING_TUTORIAL];
